import React, { useMemo } from 'react';
import SegmentedPolylineOverlay from './SegmentedPolylineOverlay';

// Helper: Generate N "pretty" ranges based on min/max and distribution
function generateFlowRateRanges(allPoints, numRanges = 5) {
    const values = allPoints
        .map(pt => pt.flowRateGpm)
        .filter(v => typeof v === 'number' && !isNaN(v) && v >= 0)
        .sort((a, b) => a - b);

    if (values.length === 0) {
        // Fallback to default if no data
        return [
            { min: 0, max: 2, color: '#d32f2f' },
            { min: 2, max: 5, color: '#f57c00' },
            { min: 5, max: 10, color: '#fbc02d' },
            { min: 10, max: 20, color: '#1976d2' },
            { min: 20, max: Infinity, color: '#388e3c' }
        ];
    }

    const min = values[0];
    const max = values[values.length - 1];

    // If all values are the same, just one range
    if (min === max) {
        return [
            { min, max: min, color: '#388e3c' }
        ];
    }

    // Use quantiles for better distribution if enough data, else equal intervals
    let breaks = [];
    if (values.length >= numRanges * 2) {
        // Quantile breaks
        for (let i = 1; i < numRanges; i++) {
            const qIdx = Math.floor((i / numRanges) * values.length);
            breaks.push(values[qIdx]);
        }
    } else {
        // Equal intervals
        const step = (max - min) / numRanges;
        for (let i = 1; i < numRanges; i++) {
            breaks.push(min + step * i);
        }
    }

    // Always ensure unique and sorted
    breaks = Array.from(new Set(breaks)).sort((a, b) => a - b);

    // Color palette (red to green)
    const colors = ['#d32f2f', '#f57c00', '#fbc02d', '#1976d2', '#388e3c'];

    // Build ranges
    const ranges = [];
    let last = min;
    for (let i = 0; i < breaks.length; i++) {
        ranges.push({
            min: last,
            max: breaks[i],
            color: colors[i] || colors[colors.length - 1]
        });
        last = breaks[i];
    }
    // Final range
    ranges.push({
        min: last,
        max: Infinity,
        color: colors[ranges.length] || colors[colors.length - 1]
    });

    return ranges;
}

const legendFormatter = r =>
    r.max === Infinity
        ? `${r.min.toFixed(3)}+`
        : `${r.min.toFixed(3)} - ${r.max.toFixed(3)}`;

export default function FlowRateOverlay({ routes }) {
    // routes: array of segments (arrays of points)
    const allPoints = useMemo(() => routes.flat(), [routes]);
    const flowRateRanges = useMemo(() => generateFlowRateRanges(allPoints), [allPoints]);

    if (!allPoints.length) return null;

    return (
        <SegmentedPolylineOverlay
            segments={routes}
            valueSelector={pt => pt.flowRateGpm >= 0 ? pt.flowRateGpm : 0}
            ranges={flowRateRanges}
            legendTitle="Flow Rate Legend (gpm)"
            legendFormatter={legendFormatter}
            units="gpm"
            discrete={false}
        />
    );
}